import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Shuffle, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PopButton } from "../PopButton";
import { OptionsEditor } from "./OptionsEditor";
import { TIMER_CHOICES } from "./Countdown";
import { SoundStylePicker } from "../SoundStylePicker";
import type { GroupState } from "./GroupParts";

export function HostPanel({ group, token, onChanged }: { group: GroupState; token: string; onChanged: () => void }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const headers = { "X-Host-Token": token };
  const canEdit = !group.rounds.length && !group.vote_count;

  const act = async (path: string) => {
    setBusy(true);
    try {
      await api.post(`/groups/${group.id}/${path}`, null, { headers });
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setTimer = async (seconds: number) => {
    setBusy(true);
    try {
      await api.post(`/groups/${group.id}/timer`, { seconds }, { headers });
      toast.success(seconds ? `Timer set — ${TIMER_CHOICES.find((t) => t.s === seconds)?.label} on the clock` : "Timer off");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setStyle = async (style: string) => {
    try {
      await api.post(`/groups/${group.id}/sound-style`, { style }, { headers });
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    if (!confirmDelete) return setConfirmDelete(true);
    try {
      await api.delete(`/groups/${group.id}`, { headers });
      toast.success("Decision deleted");
      navigate("/group/new");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const lastRound = group.mode === "normal" || group.current_round === 3;
  return (
    <div className="space-y-3 rounded-2xl border-[3px] border-ink bg-zinc-50 p-4" data-testid="group-host-panel">
      <p className="text-xs font-extrabold uppercase tracking-widest text-zinc-500">Host controls</p>
      {canEdit && !editing && (
        <PopButton tone="white" size="sm" className="w-full" onClick={() => setEditing(true)} data-testid="group-edit-options-button"><Pencil className="h-4 w-4" />Edit options</PopButton>
      )}
      {canEdit && editing && <OptionsEditor group={group} token={token} onDone={() => { setEditing(false); onChanged(); }} />}
      {!canEdit && !group.rounds.length && <p className="text-xs font-semibold text-zinc-500" data-testid="group-options-locked-note">Options are locked once the first vote is in.</p>}
      <PopButton tone="orange" className="w-full" disabled={busy || !group.vote_count} onClick={() => act("close")} data-testid="group-close-round-button">
        <Trophy className="h-4 w-4" />{lastRound ? "Close voting & reveal" : `Close round ${group.current_round}`}
      </PopButton>
      {!group.vote_count && <p className="text-center text-xs font-semibold text-zinc-500">Waiting for at least one vote.</p>}
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-zinc-500">{group.round_deadline ? "Restart the clock" : "Auto-close after"}</p>
        <div className="flex flex-wrap gap-2">
          {TIMER_CHOICES.map((t) => (
            <button key={t.s} className="chip" disabled={busy} data-active={t.s === 0 ? !group.round_deadline : group.round_deadline && group.time_limit_sec === t.s}
              onClick={() => setTimer(t.s)} data-testid={`group-host-timer-${t.s}`}>{t.s === 0 ? "Off" : t.label}</button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-zinc-500">Reveal sound style</p>
        <SoundStylePicker value={group.sound_style} onChange={setStyle} place="host" compact />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PopButton tone="yellow" size="sm" disabled={busy} onClick={() => act("dither")} data-testid="group-dither-now-button"><Shuffle className="h-4 w-4" />Dither decides</PopButton>
        <PopButton tone={confirmDelete ? "pink" : "white"} size="sm" onClick={remove} data-testid="group-delete-button"><Trash2 className="h-4 w-4" />{confirmDelete ? "Sure?" : "Delete"}</PopButton>
      </div>
    </div>
  );
}
